// src/api/branding.ts
//
// Nome, logo e colore della sidebar dell'azienda. La stessa build della SPA
// serve più aziende, ognuna con la propria istanza di API: i valori arrivano
// da GET /api/branding (APP_NAME e BRAND_* nel .env dell'API), non dal .env
// del frontend.
//
// L'ultima risposta resta in localStorage: dal secondo accesso l'app parte
// subito con quella e la aggiorna in background.

import { reactive, readonly } from 'vue'
import { http } from './http'

export interface Branding {
  name: string
  /** Logo per fondi chiari; null = al suo posto si mostra il nome. */
  logo_url: string | null
  /** Logo per fondi scuri (sidebar, tema scuro); null = si inverte logo_url. */
  logo_dark_url: string | null
  /** Sfondo della sidebar (#rrggbb); null = colore standard di CoreUI. */
  sidebar_color: string | null
}

const CHIAVE_CACHE = 'branding'

const inCache = leggiCache()

// VITE_APP_NAME vale solo finché l'API non ha mai risposto su questo browser:
// a build time decide anche il titolo di index.html e il nome della PWA.
const stato = reactive<Branding>({
  name: import.meta.env.VITE_APP_NAME || 'Gestionale',
  logo_url: null,
  logo_dark_url: null,
  sidebar_color: null,
  ...inCache,
})

/** Valori correnti, reattivi: nei template si usa `branding.name` ecc. */
export const branding = readonly(stato)

/** Nome dell'applicazione, per il codice fuori dai template. */
export function appName(): string {
  return stato.name
}

/**
 * Da chiamare prima di montare l'app. Con una copia in cache non aspetta la
 * rete; al primo accesso sì, per non mostrare il nome o il logo di un'altra azienda.
 */
export async function caricaBranding(): Promise<void> {
  applicaAlDocumento()
  const aggiornamento = aggiorna()
  if (!inCache) await aggiornamento
}

async function aggiorna(): Promise<void> {
  try {
    const { data } = await http.get<{ data: Branding }>('/branding', { timeout: 5000 })
    Object.assign(stato, data.data)
    localStorage.setItem(CHIAVE_CACHE, JSON.stringify(data.data))
    applicaAlDocumento()
  } catch (error) {
    // API irraggiungibile: si resta sulla copia in cache o sul nome di build.
    // L'errore vero lo mostrerà il login.
    console.warn('Marchio non disponibile:', error)
  }
}

/**
 * index.html ha il nome deciso a build time: va sostituito con quello
 * dell'azienda. Il colore della sidebar passa da una variabile CSS sulla
 * radice (.app-sidebar in style.scss): CSidebar non inoltra lo `style`.
 */
function applicaAlDocumento(): void {
  document.title = stato.name
  document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute('content', stato.name)

  const radice = document.documentElement.style
  if (stato.sidebar_color) radice.setProperty('--app-sidebar-bg', stato.sidebar_color)
  else radice.removeProperty('--app-sidebar-bg')
}

function leggiCache(): Branding | null {
  try {
    const valore = JSON.parse(localStorage.getItem(CHIAVE_CACHE) || 'null')
    return typeof valore?.name === 'string' ? valore : null
  } catch {
    return null
  }
}
